// 환경별 API URL 설정 관리
export interface ApiConfig {
  GATEWAY: string;
  INVENTORY: string;
  ORDER: string;
  IDENTITY: string;
  NOTIFICATION: string;
}

export interface EnvironmentConfig {
  API_URLS: ApiConfig;
  ENVIRONMENT: 'development' | 'production' | 'staging';
  LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error';
  DEFAULT_API_URL: string;
  VAPID_PUBLIC_KEY: string;
  PORTONE_STORE_ID: string;
  PORTONE_CHANNEL_KEY: string;
  // 사업자 정보
  BUSINESS_COMPANY_NAME: string;
  BUSINESS_REGISTRATION_NUMBER: string;
  BUSINESS_CEO_NAME: string;
  BUSINESS_ADDRESS: string;
  BUSINESS_PHONE_NUMBER: string;
  BUSINESS_TELECOM_NUMBER?: string;
}

// Base URL에 /api가 포함되어 있는지 확인하는 유틸리티 함수
export const baseUrlIncludesApi = (baseUrl: string): boolean => {
  // 더 정확한 체크: /api로 끝나거나 /api/로 끝나거나 /api/가 중간에 포함된 경우
  return baseUrl.includes('/api') && (
    baseUrl.endsWith('/api') || 
    baseUrl.endsWith('/api/') || 
    baseUrl.includes('/api/')
  );
};

// Base URL에서 /api를 제거하는 유틸리티 함수
export const removeApiFromBaseUrl = (baseUrl: string): string => {
  if (baseUrl.endsWith('/api/')) {
    return baseUrl.slice(0, -4);
  }
  if (baseUrl.endsWith('/api')) {
    return baseUrl.slice(0, -4);
  }
  return baseUrl;
};

// Base URL에 /api를 추가하는 유틸리티 함수
export const addApiToBaseUrl = (baseUrl: string): string => {
  if (baseUrlIncludesApi(baseUrl)) {
    return baseUrl;
  }
  return baseUrl.endsWith('/') ? `${baseUrl}api` : `${baseUrl}/api`;
};

/** 개발자 모드: localhost:3000에서 실행 중일 때 true. 사업자정보 등록 등 절차 없이 대시보드 진입 허용용 */
export const isDeveloperMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' && window.location.port === '3000';
};

// 환경변수 미설정 시 에러를 throw하는 함수
const getEnvOrThrow = (key: string): string => {
  // CRA 환경에서는 process.env 사용
  const value = process.env[key];
  if (!value) {
    console.error(`❌ 환경변수 ${key}가 정의되어 있지 않습니다.`);
    console.error(`�� .env 파일을 확인하거나 환경변수를 설정해주세요.`);
    throw new Error(`환경변수 ${key}가 정의되어 있지 않습니다. .env 파일을 확인하세요.`);
  }
  return value;
};

// 모듈 로드 시점에서 즉시 환경변수 로드 (localhost 전용)
const initializeEnvironmentVariables = (): Record<string, string> => {
  let vars: Record<string, string> = {};

  // **진짜 localhost에서만** customEnvLoader 실행
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isRealLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isRealLocalhost) {
      console.log('🛠️ [초기화] localhost 환경변수 즉시 로드 시작');

      try {
        // XMLHttpRequest로 동기 로딩
        const xhr = new XMLHttpRequest();
        xhr.open('GET', '/env.development.json', false); // false = 동기 모드
        xhr.send();

        if (xhr.status === 200) {
          const envData = JSON.parse(xhr.responseText);
          vars = { ...vars, ...envData };
          console.log('✅ [초기화] env.development.json 즉시 로드 완료:', Object.keys(envData).length, '개');
        } else {
          console.warn('⚠️ [초기화] env.development.json 파일을 찾을 수 없습니다');
        }
      } catch (error) {
        console.warn('⚠️ [초기화] 환경변수 즉시 로드 실패:', error);
      }
    } else {
      // 프로덕션 환경에서는 초기화 로그 최소화
      
      try {
        // 프로덕션 환경에서도 환경변수 로드
        const xhr = new XMLHttpRequest();
        xhr.open('GET', '/env.production.json', false); // false = 동기 모드
        xhr.send();

        if (xhr.status === 200) {
          const envData = JSON.parse(xhr.responseText);
          vars = { ...vars, ...envData };
          // 프로덕션에서는 간단한 로그만
        } else {
          console.warn('⚠️ [초기화] env.production.json 파일을 찾을 수 없습니다');
        }
      } catch (error) {
        console.warn('⚠️ [초기화] 프로덕션 환경변수 즉시 로드 실패:', error);
      }
    }
  }

  return vars;
};

// 커스텀 환경변수 저장소 (모듈 로드 시점에서 즉시 초기화)
export let customEnvVars: Record<string, string> = initializeEnvironmentVariables();

// 환경변수 캐시 (메모이제이션)
const envCache: Record<string, string> = {};

// 환경변수 캐시 초기화 함수
const clearEnvCache = (): void => {
  Object.keys(envCache).forEach(key => delete envCache[key]);
};

// 커스텀 환경변수 설정 함수 (다른 모듈에서 사용)
export const setCustomEnvVars = (vars: Record<string, string>): void => {
  customEnvVars = { ...customEnvVars, ...vars };
  // 환경변수가 변경되면 캐시 초기화
  clearEnvCache();
};

// 커스텀 환경변수 가져오기 함수
export const getCustomEnvVars = (): Record<string, string> => {
  return { ...customEnvVars };
};

// 환경변수 미설정 시 기본값을 반환하는 함수 (캐싱 적용)
const getEnvOrDefault = (key: string, defaultValue: string): string => {
  // 캐시에 이미 있으면 바로 반환
  if (envCache[key] !== undefined) {
    return envCache[key];
  }

  const isProduction = process.env.NODE_ENV === 'production';
  
  // 프로덕션 환경에서는 디버깅 로그 최소화
  if (!isProduction) {
    console.log(`🔍 [환경변수] ${key} 검색 시작...`);
    console.log(`🔍 [환경변수] 커스텀 환경변수 전체:`, customEnvVars);
    console.log(`🔍 [환경변수] process.env 전체:`, process.env);
    console.log(`🔍 [환경변수] 현재 호스트명:`, window.location.hostname);
    console.log(`🔍 [환경변수] 현재 환경:`, process.env.NODE_ENV);
  }
  
  let result: string;
  
  // 1. 커스텀 환경변수에서 먼저 확인
  if (customEnvVars[key]) {
    if (!isProduction) {
      console.log(`🔍 [환경변수] ${key} 커스텀 환경변수에서 발견:`, customEnvVars[key]);
    }
    result = customEnvVars[key];
  }
  // 2. process.env에서 확인
  else if (process.env[key]) {
    if (!isProduction) {
      console.log(`🔍 [환경변수] ${key} process.env에서 발견:`, process.env[key]);
    }
    result = process.env[key]!;
  }
  // 3. 기본값 사용
  else {
    if (!isProduction) {
      console.log(`🔍 [환경변수] ${key} 환경변수 없음, 기본값 사용:`, defaultValue);
    }
    // 환경변수 누락은 프로덕션에서도 경고 필요
    console.warn(`⚠️ 환경변수 ${key}가 설정되지 않아 기본값을 사용합니다: ${defaultValue}`);
    result = defaultValue;
  }
  
  // 캐시에 저장
  envCache[key] = result;
  return result;
};

// 포트 번호 동적 처리 함수
const getPort = (service: string): number => {
  const isDev = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // 환경변수에서 포트 가져오기
  const portMap: Record<string, number> = {
    gateway: parseInt(process.env.REACT_APP_GATEWAY_PORT || '5000'),
    inventory: parseInt(process.env.REACT_APP_INVENTORY_PORT || '5001'),
    order: parseInt(process.env.REACT_APP_ORDER_PORT || '5192'),
    identity: parseInt(process.env.REACT_APP_IDENTITY_PORT || '5007'),
    notification: parseInt(process.env.REACT_APP_NOTIFICATION_PORT || '5004')
  };

  return portMap[service] || 5000;
};

// 동적 URL 생성 함수 (Base URL에 /api 미포함)
const generateApiUrl = (service: string, port?: number): string => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname :
    (process.env.REACT_APP_API_HOST || 'localhost');
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const actualPort = port || getPort(service);

  if (isLocal) {
    return `http://${hostname}:${actualPort}`;
  } else {
    // 프로덕션에서는 현재 도메인 사용, 포트는 443(HTTPS 기본)
    return `https://${hostname}`;
  }
};

const environments: Record<string, EnvironmentConfig> = {
  development: {
    API_URLS: {
      GATEWAY: getEnvOrDefault('REACT_APP_DEV_API_GATEWAY_URL', generateApiUrl('gateway')),
      INVENTORY: getEnvOrDefault('REACT_APP_DEV_INVENTORY_API_URL', generateApiUrl('inventory')),
      ORDER: getEnvOrDefault('REACT_APP_DEV_ORDER_API_URL', generateApiUrl('order')),
      IDENTITY: getEnvOrDefault('REACT_APP_DEV_IDENTITY_API_URL', generateApiUrl('identity')),
      NOTIFICATION: getEnvOrDefault('REACT_APP_DEV_NOTIFICATION_API_URL', generateApiUrl('notification')),
    },
    ENVIRONMENT: 'development',
    LOG_LEVEL: 'debug',
    DEFAULT_API_URL: getEnvOrDefault('REACT_APP_DEV_API_GATEWAY_URL', generateApiUrl('gateway')),
    VAPID_PUBLIC_KEY: getEnvOrDefault('REACT_APP_VAPID_PUBLIC_KEY', 'BKhE-ykBuZY26rLp8R1OO6KtQaczwTXAGYbfM-MfeLdkgV9I1gdeP5AxfNIUgdsmzlLKuvp8ihS12FI8GgirUNQ'),
    PORTONE_STORE_ID: getEnvOrDefault('REACT_APP_PORTONE_STORE_ID', ''),
    PORTONE_CHANNEL_KEY: getEnvOrDefault('REACT_APP_PORTONE_CHANNEL_KEY', ''),
    BUSINESS_COMPANY_NAME: getEnvOrDefault('REACT_APP_BUSINESS_COMPANY_NAME', ''),
    BUSINESS_REGISTRATION_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_REGISTRATION_NUMBER', ''),
    BUSINESS_CEO_NAME: getEnvOrDefault('REACT_APP_BUSINESS_CEO_NAME', ''),
    BUSINESS_ADDRESS: getEnvOrDefault('REACT_APP_BUSINESS_ADDRESS', ''),
    BUSINESS_PHONE_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_PHONE_NUMBER', ''),
    BUSINESS_TELECOM_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_TELECOM_NUMBER', ''),
  },
  production: {
    // 프로덕션에서는 빌드 시 주입된 환경변수 대신 현재 접속한 호스트명을 기준으로 URL을 구성한다.
    // 이렇게 해야 배포 대상(예: app.judam.kr, admin.judam.kr 등)에 따라 자동으로 올바른 API 도메인이 사용된다.
    API_URLS: {
      GATEWAY: generateApiUrl('gateway'),
      INVENTORY: generateApiUrl('inventory'),
      ORDER: generateApiUrl('order'),
      IDENTITY: generateApiUrl('identity'),
      NOTIFICATION: generateApiUrl('inventory'), // 푸시 알림은 InventoryManagement 서비스에서 실행됨
    },
    ENVIRONMENT: 'production',
    LOG_LEVEL: 'error',
    DEFAULT_API_URL: generateApiUrl('gateway'),
    VAPID_PUBLIC_KEY: getEnvOrDefault('REACT_APP_VAPID_PUBLIC_KEY', 'BKhE-ykBuZY26rLp8R1OO6KtQaczwTXAGYbfM-MfeLdkgV9I1gdeP5AxfNIUgdsmzlLKuvp8ihS12FI8GgirUNQ'),
    PORTONE_STORE_ID: getEnvOrDefault('REACT_APP_PORTONE_STORE_ID', ''),
    PORTONE_CHANNEL_KEY: getEnvOrDefault('REACT_APP_PORTONE_CHANNEL_KEY', ''),
    BUSINESS_COMPANY_NAME: getEnvOrDefault('REACT_APP_BUSINESS_COMPANY_NAME', ''),
    BUSINESS_REGISTRATION_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_REGISTRATION_NUMBER', ''),
    BUSINESS_CEO_NAME: getEnvOrDefault('REACT_APP_BUSINESS_CEO_NAME', ''),
    BUSINESS_ADDRESS: getEnvOrDefault('REACT_APP_BUSINESS_ADDRESS', ''),
    BUSINESS_PHONE_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_PHONE_NUMBER', ''),
    BUSINESS_TELECOM_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_TELECOM_NUMBER', ''),
  },
  staging: {
    API_URLS: {
      GATEWAY: getEnvOrDefault('REACT_APP_STAGING_API_GATEWAY_URL', 'https://staging.judam.kr'),
      INVENTORY: getEnvOrDefault('REACT_APP_STAGING_INVENTORY_API_URL', 'https://staging.judam.kr'),
      ORDER: getEnvOrDefault('REACT_APP_STAGING_ORDER_API_URL', 'https://staging.judam.kr'),
      IDENTITY: getEnvOrDefault('REACT_APP_STAGING_IDENTITY_API_URL', 'https://staging.judam.kr'),
      NOTIFICATION: getEnvOrDefault('REACT_APP_STAGING_NOTIFICATION_API_URL', 'https://staging.judam.kr'),
    },
    ENVIRONMENT: 'staging',
    LOG_LEVEL: 'info',
    DEFAULT_API_URL: getEnvOrDefault('REACT_APP_STAGING_API_GATEWAY_URL', 'https://staging.judam.kr'),
    VAPID_PUBLIC_KEY: getEnvOrDefault('REACT_APP_VAPID_PUBLIC_KEY', 'BKhE-ykBuZY26rLp8R1OO6KtQaczwTXAGYbfM-MfeLdkgV9I1gdeP5AxfNIUgdsmzlLKuvp8ihS12FI8GgirUNQ'),
    PORTONE_STORE_ID: getEnvOrDefault('REACT_APP_PORTONE_STORE_ID', ''),
    PORTONE_CHANNEL_KEY: getEnvOrDefault('REACT_APP_PORTONE_CHANNEL_KEY', ''),
    BUSINESS_COMPANY_NAME: getEnvOrDefault('REACT_APP_BUSINESS_COMPANY_NAME', ''),
    BUSINESS_REGISTRATION_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_REGISTRATION_NUMBER', ''),
    BUSINESS_CEO_NAME: getEnvOrDefault('REACT_APP_BUSINESS_CEO_NAME', ''),
    BUSINESS_ADDRESS: getEnvOrDefault('REACT_APP_BUSINESS_ADDRESS', ''),
    BUSINESS_PHONE_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_PHONE_NUMBER', ''),
    BUSINESS_TELECOM_NUMBER: getEnvOrDefault('REACT_APP_BUSINESS_TELECOM_NUMBER', ''),
  },
};

// 현재 환경 감지 (hostname 우선)
const getCurrentEnvironment = (): string => {
  // 1. 호스트명 기반 판단 (최우선 - 실제 배포 환경 감지)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // 프로덕션 환경
    if (hostname === 'judam.kr' || hostname.endsWith('.judam.kr')) {
      if (hostname.includes('staging')) {
        return 'staging';
      }
      return 'production';
    }

    // 로컬 개발 환경
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }

    // 기타 개발 환경 (IP 주소 등)
    if (hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
      return 'development';
    }
  }

  // 2. 명시적 환경변수 확인 (빌드 시 설정)
  if (process.env.REACT_APP_ENVIRONMENT) {
    const env = process.env.REACT_APP_ENVIRONMENT.toLowerCase();
    if (['development', 'staging', 'production'].includes(env)) {
      console.log(`�� 명시적 환경변수 사용: ${env}`);
      return env;
    }
  }

  // 3. NODE_ENV 확인 (fallback)
  if (process.env.NODE_ENV === 'production') {
    return 'production';
  }
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }

  // 4. 기본값은 production (안전한 방향)
  console.warn('⚠️ 환경 감지 실패 - production으로 기본 설정');
  return 'production';
};

// 현재 환경 설정 가져오기
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const currentEnv = getCurrentEnvironment();
  const config = environments[currentEnv] || environments.development;

  console.log(`🌍 현재 환경: ${currentEnv}`);
  console.log('🔧 API 설정:', config.API_URLS);

  return config;
};

// 커스텀 환경변수 로드 함수 (개발 모드 전용)
const loadCustomEnvironmentVars = async (): Promise<void> => {
  const currentEnv = getCurrentEnvironment();

  // 개발 모드에서만 JSON 파일 로드
  if (currentEnv !== 'development') {
    console.log(`🏭 프로덕션 모드: 기존 환경변수 시스템 사용 (env.${currentEnv}.json 로드 안함)`);
    return;
  }

  try {
    const response = await fetch(`/env.${currentEnv}.json`);
    if (response.ok) {
      const envData = await response.json();
      customEnvVars = { ...customEnvVars, ...envData };
      console.log(`✅ [개발 모드] env.${currentEnv}.json에서 환경변수 로드됨:`, Object.keys(envData));
    } else {
      console.warn(`⚠️ [개발 모드] env.${currentEnv}.json 파일을 찾을 수 없습니다. 기본값을 사용합니다.`);
    }
  } catch (error) {
    console.warn(`⚠️ [개발 모드] 환경변수 파일 로드 실패:`, error);
  }
};

// 환경변수는 이미 모듈 로드 시점에서 초기화됨 (initializeEnvironmentVariables)

// 현재 환경 설정
export const config = getEnvironmentConfig();

// 환경변수 다시 로드하는 함수 (개발용)
export const reloadEnvironmentConfig = async (): Promise<EnvironmentConfig> => {
  await loadCustomEnvironmentVars();
  const newConfig = getEnvironmentConfig();
  Object.assign(config, newConfig);
  console.log('🔄 환경설정이 다시 로드되었습니다:', newConfig);
  return newConfig;
};

// 편의 함수들
export const getApiUrl = (type: keyof ApiConfig = 'GATEWAY'): string => {
  return config.API_URLS[type];
};

// API URL을 안전하게 가져오는 함수 (Base URL에 /api 포함 여부 확인)
export const getSafeApiUrl = (type: keyof ApiConfig = 'GATEWAY'): string => {
  const baseUrl = config.API_URLS[type];
  const isProduction = process.env.NODE_ENV === 'production';

  // Base URL에 /api가 이미 포함되어 있는지 확인
  if (baseUrlIncludesApi(baseUrl)) {
    // 이미 /api가 포함된 경우 경고 없이 반환
    return baseUrl;
  } else {
    const urlWithApi = addApiToBaseUrl(baseUrl);
    // 프로덕션에서는 경고 로그 최소화
    if (!isProduction) {
      console.log(`⚠️ ${type} Base URL에 /api가 없어서 추가했습니다: ${baseUrl} → ${urlWithApi}`);
    }
    return urlWithApi;
  }
};

export const getVapidPublicKey = (): string => {
  return config.VAPID_PUBLIC_KEY;
};

export const isProduction = (): boolean => {
  return config.ENVIRONMENT === 'production';
};

export const isDevelopment = (): boolean => {
  return config.ENVIRONMENT === 'development';
};

export const isStaging = (): boolean => {
  return config.ENVIRONMENT === 'staging';
};

// PortOne 설정 가져오기
export const getPortOneStoreId = (): string => {
  return config.PORTONE_STORE_ID;
};

export const getPortOneChannelKey = (): string => {
  return config.PORTONE_CHANNEL_KEY;
};

// 사업자 정보 가져오기
export const getBusinessInfo = () => {
  return {
    companyName: config.BUSINESS_COMPANY_NAME,
    businessRegistrationNumber: config.BUSINESS_REGISTRATION_NUMBER,
    ceoName: config.BUSINESS_CEO_NAME,
    businessAddress: config.BUSINESS_ADDRESS,
    phoneNumber: config.BUSINESS_PHONE_NUMBER,
    telecommunicationSalesNumber: config.BUSINESS_TELECOM_NUMBER || undefined,
  };
};

export const hasBusinessInfo = (): boolean => {
  return !!(
    config.BUSINESS_COMPANY_NAME &&
    config.BUSINESS_REGISTRATION_NUMBER &&
    config.BUSINESS_CEO_NAME &&
    config.BUSINESS_ADDRESS &&
    config.BUSINESS_PHONE_NUMBER
  );
};

// 로그 레벨 확인
export const shouldLog = (level: 'debug' | 'info' | 'warn' | 'error'): boolean => {
  const levels = ['debug', 'info', 'warn', 'error'];
  const currentLevelIndex = levels.indexOf(config.LOG_LEVEL);
  const requestedLevelIndex = levels.indexOf(level);

  return requestedLevelIndex >= currentLevelIndex;
};

// 런타임에서 API URL 변경 (브라우저 콘솔용)
if (typeof window !== 'undefined') {
  const currentEnv = getCurrentEnvironment();

  (window as any).judamConfig = {
    getCurrentConfig: () => config,
    getCustomEnvVars: () => customEnvVars,
    getCurrentEnvironment: () => currentEnv,
    setApiUrl: (type: keyof ApiConfig, url: string) => {
      config.API_URLS[type] = url;
      console.log(`✅ ${type} API URL이 ${url}로 변경되었습니다.`);
      console.log('현재 설정:', config.API_URLS);
    },
    setCustomEnvVar: (key: string, value: string) => {
      if (currentEnv === 'development') {
        customEnvVars[key] = value;
        console.log(`✅ [개발 모드] 커스텀 환경변수 ${key}이 ${value}로 설정되었습니다.`);
      } else {
        console.warn(`⚠️ [프로덕션 모드] 커스텀 환경변수 수정은 개발 모드에서만 가능합니다.`);
      }
    },
    reloadConfig: async () => {
      if (currentEnv === 'development') {
        return await reloadEnvironmentConfig();
      } else {
        console.warn(`⚠️ [프로덕션 모드] 환경설정 다시 로드는 개발 모드에서만 가능합니다.`);
        return config;
      }
    },
    resetToDefault: () => {
      const defaultConfig = getEnvironmentConfig();
      Object.assign(config, defaultConfig);
      console.log('✅ 설정이 기본값으로 재설정되었습니다.');
      console.log('현재 설정:', config.API_URLS);
    },
    // 새로운 유틸리티 함수들 추가
    baseUrlIncludesApi,
    removeApiFromBaseUrl,
    addApiToBaseUrl,
    getSafeApiUrl
  };

  if (currentEnv === 'development') {
    console.log('��️ [개발 모드] 주담 설정 도구가 로드되었습니다. 브라우저 콘솔에서 window.judamConfig를 사용하세요.');
  } else {
    console.log('�� [프로덕션 모드] 기본 설정 도구가 로드되었습니다. window.judamConfig.getCurrentConfig()로 확인 가능합니다.');
  }
}
